import { useState, useEffect, useRef, useCallback, createContext, useContext, ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { useAuth } from '../hooks/auth';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcuts';

interface LayoutContextType {
  collapsed: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  sidebarWidth: number;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a Layout component');
  }
  return context;
}

interface LayoutProps {
  children: ReactNode;
  currentTheme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  onShowShortcutsHelp?: () => void;
}

export function Layout({
  children,
  currentTheme = 'dark',
  onToggleTheme,
  onShowShortcutsHelp: _onShowShortcutsHelp,
}: LayoutProps) {
  const { user } = useAuth();
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Keyboard shortcuts modal state
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  // Global keyboard shortcut for help (Shift + / = ?)
  useKeyboardShortcut('/', () => setShowShortcutsHelp(true), { shift: true });

  // Sidebar collapsed state - по умолчанию скрыта (64px)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebarCollapsed') !== 'false';
    }
    return true;
  });

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  const openSidebar = useCallback(() => {
    setCollapsed(false);
  }, []);

  const closeSidebar = useCallback(() => {
    setCollapsed(true);
  }, []);

  const toggleSidebar = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  const sidebarWidth = collapsed ? 64 : 240;

  const contextValue: LayoutContextType = {
    collapsed,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    sidebarWidth,
  };

  if (!user) return null;

  return (
    <LayoutContext.Provider value={contextValue}>
      <div className="h-screen flex bg-background overflow-hidden">
        {/* Sidebar */}
        <Sidebar
          currentTheme={currentTheme}
          onToggleTheme={onToggleTheme}
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          onShowShortcutsHelp={() => setShowShortcutsHelp(true)}
        />

        {/* Main Content with dynamic margin (only on desktop) */}
        <div
          ref={mainContentRef}
          className="flex-1 flex flex-col min-w-0 overflow-hidden transition-all duration-300 ease-in-out main-content-margin"
          style={{ ['--sidebar-margin' as string]: `${sidebarWidth}px` }}
        >
          {children}
        </div>

        {/* Keyboard Shortcuts Help Modal */}
        {showShortcutsHelp && (
          <KeyboardShortcutsHelp
            isOpen={showShortcutsHelp}
            onClose={() => setShowShortcutsHelp(false)}
          />
        )}
      </div>
    </LayoutContext.Provider>
  );
}

export default Layout;
